import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Mail, Lock, User, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { cn } from '@/lib/utils';

export type AuthMode = 'login' | 'register';

const UA_BLUE = '#0057B7';
const UA_YELLOW = '#FFD700';

interface AuthModalProps {
  open: boolean;
  mode: AuthMode;
  onClose: () => void;
  onModeChange: (mode: AuthMode) => void;
}

export function AuthModal({ open, mode, onClose, onModeChange }: AuthModalProps) {
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);
  const loading = useAuthStore((s) => s.loading);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      setPassword('');
    }
  }, [open]);

  useEffect(() => {
    setError(null);
  }, [mode]);

  const submit = async () => {
    setError(null);
    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else {
        await register({ email: email.trim(), password, fullName: fullName.trim() || undefined });
      }
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Помилка');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            className="relative w-full max-w-md bg-[#0c0c0e] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="h-1.5"
              style={{ background: `linear-gradient(90deg, ${UA_BLUE} 0%, ${UA_YELLOW} 100%)` }}
            />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/5 transition-colors z-10"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white tracking-tight">
                  {mode === 'login' ? 'З поверненням!' : 'Створення акаунта'}
                </h2>
                <p className="text-sm text-white/40 mt-1">
                  {mode === 'login'
                    ? 'Увійдіть, щоб продовжити роботу зі своїми справами.'
                    : 'Безкоштовно. Без прив`язки до банку.'}
                </p>
              </div>

              <form
                onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="space-y-4"
              >
                {mode === 'register' && (
                  <Field
                    icon={User}
                    label="ПІБ (необовʼязково)"
                    placeholder="Іван Петренко"
                    value={fullName}
                    onChange={setFullName}
                    autoFocus
                    type="text"
                  />
                )}
                <Field
                  icon={Mail}
                  label="E-mail"
                  placeholder="you@example.com"
                  value={email}
                  onChange={setEmail}
                  autoFocus={mode === 'login'}
                  type="email"
                  required
                />
                <Field
                  icon={Lock}
                  label="Пароль"
                  placeholder="мінімум 6 символів"
                  value={password}
                  onChange={setPassword}
                  type="password"
                  required
                />

                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-300">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-[#0a0a0c] shadow-lg disabled:opacity-50 transition-transform hover:scale-[1.01]"
                  style={{ background: UA_YELLOW }}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {mode === 'login' ? 'Увійти' : 'Зареєструватися'}
                </button>
              </form>

              <div className="mt-6 text-center text-sm text-white/40">
                {mode === 'login' ? (
                  <>
                    Немає акаунта?{' '}
                    <button
                      onClick={() => onModeChange('register')}
                      className="font-semibold underline-offset-4 hover:underline"
                      style={{ color: UA_YELLOW }}
                    >
                      Створити
                    </button>
                  </>
                ) : (
                  <>
                    Вже є акаунт?{' '}
                    <button
                      onClick={() => onModeChange('login')}
                      className="font-semibold underline-offset-4 hover:underline"
                      style={{ color: UA_YELLOW }}
                    >
                      Увійти
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({
  icon: Icon,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  autoFocus,
  required,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  required?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-white/40 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
        <input
          autoFocus={autoFocus}
          required={required}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(
            'w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white',
            'placeholder:text-white/30 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/30 transition-colors'
          )}
        />
      </div>
    </div>
  );
}
